import { Tabs, Stack, Text, Badge, Group } from '@mantine/core';
import { MediaUploader } from './MediaUploader';
import { useState } from 'react';

interface MediaUrl {
	language: string;
	url: string;
	fileId?: string;
}

interface MultilingualMediaUploaderProps {
	exerciseType: 'AUDIO' | 'VIDEO';
	languages: readonly string[];
	languageLabels: Record<string, string>;
	value?: {
		duration: number;
		mediaUrls: MediaUrl[];
	};
	onChange: (value: { duration: number; mediaUrls: MediaUrl[] } | null) => void;
	onFileIdChange?: (fileId: string | null) => void;
}

export function MultilingualMediaUploader({
	exerciseType,
	languages,
	languageLabels,
	value,
	onChange,
	onFileIdChange,
}: MultilingualMediaUploaderProps) {
	const [activeTab, setActiveTab] = useState<string>(languages[0] as string);

	console.log('[MultilingualMediaUploader] Received value:', value);
	console.log('[MultilingualMediaUploader] mediaUrls:', value?.mediaUrls);

	// Get media URL for a specific language
	const getMediaForLanguage = (lang: string) => {
		const mediaUrls = value?.mediaUrls || [];
		console.log('[MultilingualMediaUploader] getMediaForLanguage', lang, 'mediaUrls:', mediaUrls);
		const mediaUrl = mediaUrls.find((m) => m.language === lang);
		if (!mediaUrl) return undefined;

		return {
			url: mediaUrl.url,
			duration: value?.duration || 0,
			fileId: mediaUrl.fileId,
		};
	};

	// Update media URL for a specific language
	const handleLanguageChange = (lang: string, mediaData: { url: string; duration: number; fileId: string } | null) => {
		const currentMediaUrls = value?.mediaUrls || [];

		if (mediaData === null) {
			// Remove media for this language
			const updatedUrls = currentMediaUrls.filter((m) => m.language !== lang);

			if (updatedUrls.length === 0) {
				onChange(null);
			} else {
				onChange({
					duration: value?.duration || 0,
					mediaUrls: updatedUrls,
				});
			}
		} else {
			// Add or update media for this language
			const existingIndex = currentMediaUrls.findIndex((m) => m.language === lang);
			let updatedUrls: MediaUrl[];

			if (existingIndex >= 0) {
				// Update existing
				updatedUrls = currentMediaUrls.map((m, idx) =>
					idx === existingIndex
						? { language: lang, url: mediaData.url, fileId: mediaData.fileId }
						: m
				);
			} else {
				// Add new
				updatedUrls = [
					...currentMediaUrls,
					{ language: lang, url: mediaData.url, fileId: mediaData.fileId },
				];
			}

			onChange({
				duration: mediaData.duration,
				mediaUrls: updatedUrls,
			});
		}
	};

	// Count how many languages have media uploaded
	const uploadedCount = value?.mediaUrls?.length || 0;

	return (
		<Stack gap="md">
			<Group gap="xs">
				<Text size="sm" fw={500}>
					{exerciseType === 'VIDEO' ? 'Video' : 'Audio'} Files
				</Text>
				<Badge size="sm" color="gray" variant="light">
					{uploadedCount} / {languages.length} languages
				</Badge>
			</Group>

			<Tabs value={activeTab} onChange={(val) => setActiveTab(val as string)}>
				<Tabs.List>
					{languages.map((lang) => {
						return (
							<Tabs.Tab
								key={lang}
								value={lang}
							>
								{languageLabels[lang]}
							</Tabs.Tab>
						);
					})}
				</Tabs.List>

				{languages.map((lang) => (
					<Tabs.Panel key={lang} value={lang} pt="md">
						<MediaUploader
							exerciseType={exerciseType}
							language={lang}
							value={getMediaForLanguage(lang)}
							onChange={(mediaData) => handleLanguageChange(lang, mediaData)}
							onFileIdChange={onFileIdChange}
						/>
					</Tabs.Panel>
				))}
			</Tabs>
		</Stack>
	);
}
